import { useCallback } from 'react';

import { MessagingChannelType, useAblyChannel, usePusherChannel } from '@dutchie-pos/messaging';

type UsePayByBankMessagingProps = {
  enabled: boolean;
  shipmentId: number;
  onCartOpened: () => void;
  onCartConfirmed: () => void;
};

export function usePayByBankMessaging({ enabled, shipmentId, onCartOpened, onCartConfirmed }: UsePayByBankMessagingProps) {
  // useAblyChannel (unlike usePusherChannel) has no enabled param — it subscribes on
  // mount and stays active for the component's lifetime. Guarding here ensures a
  // late-arriving Ably message while the QR screen is closed doesn't still fire
  // the callbacks.
  const handleMessage = useCallback(
    (eventName: string, data: string | object) => {
      if (!enabled) {
        return;
      }
      if (eventName !== 'cart-opened' && eventName !== 'cart-confirmed') {
        return;
      }

      // Only react to events for this session's shipment, so a second cart at the same
      // location doesn't trip this one. Backward-compatible: if the payload carries no
      // shipment_id (older backend), don't filter.
      let payload: { shipment_id?: number } | null;
      if (typeof data === 'string') {
        try {
          payload = JSON.parse(data);
        } catch {
          payload = null;
        }
      } else {
        payload = data as { shipment_id?: number };
      }
      const eventShipmentId = payload?.shipment_id;
      if (eventShipmentId != null && eventShipmentId !== shipmentId) {
        return;
      }

      if (eventName === 'cart-opened') {
        onCartOpened();
      } else {
        onCartConfirmed();
      }
    },
    [enabled, shipmentId, onCartOpened, onCartConfirmed],
  );

  usePusherChannel({
    channelType: MessagingChannelType.DutchiePayMobileCheckout,
    enabled,
    onChannelMessage: handleMessage,
  });

  useAblyChannel(MessagingChannelType.DutchiePayMobileCheckout, handleMessage);
}
